import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://www.mobilassistance.com';

/**
 * Enhanced SEO PageContainer Component
 * - Dynamic meta tags for each page
 * - Open Graph and Twitter Card support
 * - Structured Data (JSON-LD) support
 * - Canonical URLs
 * - Google, Microsoft, and other search engines compliant
 */
const PageContainer = ({ 
  title, 
  description, 
  keywords, 
  image, 
  type = 'website',
  structuredData,
  children 
}) => {
  const location = useLocation();
  const fullUrl = `${SITE_URL}${location.pathname}`;
  const pageTitle = title ? `${title} | Mobil Assistance` : 'Mobil Assistance';
  
  // Description fallback logic - SEO optimized
  // Ensure description is always a valid string
  let pageDescription = '6 farklı hizmet alanında hizmet veren Sektörünün öncüsü ve lideri olan Mobil Assistance';
  if (description !== undefined && description !== null && description !== '') {
    const trimmedDesc = String(description).trim();
    if (trimmedDesc !== '') {
      pageDescription = trimmedDesc;
    }
  }
  
  const pageImage = image || `${SITE_URL}/assets/img/slider/mobil-assistance-slider-1.jpg`;

  // Default structured data for organization
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Mobil Assistance',
    url: SITE_URL,
    logo: `${SITE_URL}/assets/img/MobilAssistance.svg`,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+90-850-255-02-03',
      contactType: 'customer service',
      areaServed: 'TR',
      availableLanguage: 'Turkish'
    },
    sameAs: [
      'https://www.facebook.com/mobilassistance',
      'https://www.instagram.com/mobilassistance/',
      'https://www.linkedin.com/company/mobil-assistance/',
      'https://x.com/mobilassistance',
      'https://www.youtube.com/channel/UCLg4MfWfFBykwkieU9AKgqA'
    ]
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <div>
      <Helmet>
        {/* Basic Meta Tags - Google, Bing, Yandex compliant */}
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {keywords && <meta name="keywords" content={keywords} />}
        
        {/* Language and Locale */}
        <html lang="tr" />
        <meta property="og:locale" content="tr_TR" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content={type} />
        <meta property="og:url" content={fullUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Mobil Assistance" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={fullUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />
        <meta name="twitter:site" content="@mobilassistance" />
        
        {/* Microsoft/Bing specific */}
        <meta name="msapplication-TileColor" content="#ff7e00" />
        <meta name="theme-color" content="#ff7e00" />
        
        {/* Canonical URL - Prevents duplicate content issues */}
        <link rel="canonical" href={fullUrl} />
        
        {/* Structured Data (JSON-LD) - Google Rich Results */}
        <script type="application/ld+json">
          {JSON.stringify(finalStructuredData)}
        </script>
      </Helmet>
      {children}
    </div>
  );
};

PageContainer.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.string,
  image: PropTypes.string,
  type: PropTypes.oneOf(['website', 'article', 'product']),
  structuredData: PropTypes.object,
  children: PropTypes.node.isRequired,
};

export default PageContainer;

